import React, { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import isValid from 'date-fns/isValid';
import parse from 'date-fns/parse';
import format from 'date-fns/format';

const DateDropdown = ({ id, name, className, maxDate, minDate, dateFormat, onChange, showToolTip, tooltipContent, value }) => {
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const convertToDate = date => (date !== null && isValid(parse(date, dateFormat, new Date())) ? parse(date, dateFormat, new Date()) : null);
  const [selectedDay, setSelectedDay] = useState(value ? convertToDate(value).getDate() : null);
  const [selectedYear, setSelectedYear] = useState(value ? convertToDate(value).getFullYear() : null);
  const [selectedMonth, setSelectedMonth] = useState(value ? months[convertToDate(value).getMonth()] : null);
  const [minimumDate, setMinimumDate] = useState(convertToDate(minDate));
  const [maximumDate, setMaximumDate] = useState(convertToDate(maxDate));
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const years = Array.from(new Array(100), (val, index) => currentYear - index);
  const [fullDate, setFullDate] = useState(value !== null ? value : null);

  const getDaysArray = (year, month) => {
    const yearSelected = year !== null ? year : currentYear;
    const monthIndex = month !== null ? month + 1 : currentMonth + 1;

    const dateObject = new Date(yearSelected, monthIndex, 0);
    return Array.from({ length: dateObject.getDate() }, (_, i) => i + 1);
  };

  const [days, setDays] = useState(getDaysArray(currentYear, currentMonth));

  const setValidDate = (year, month, day) => {
    if (!year || (month === null || month.length <= 0) || !day) {
      if (fullDate) {
        onChange(null);
        setFullDate(null);
      }
      return;
    }

    const date = new Date(year, month, day);

    if (maximumDate !== null && date > maximumDate) {
      if (fullDate) {
        onChange(null);
        setFullDate(null);
      }
      return;
    }

    if (minimumDate !== null && date < minimumDate) {
      if (fullDate) {
        onChange(null);
        setFullDate(null);
      }
      return;
    }

    onChange(format(date, dateFormat));
    setFullDate(format(date, dateFormat));
  };

  const changeYear = (year) => {
    const monthIndex = selectedMonth !== null ? months.indexOf(selectedMonth) : null;
    setDays(getDaysArray(year, monthIndex));
    setSelectedYear(year);
    setValidDate(year, monthIndex, selectedDay);
  };

  const changeMonth = (month) => {
    const monthIndex = month ? months.indexOf(month) : '';
    setDays(getDaysArray(selectedYear, months.indexOf(month)));
    setSelectedMonth(month);
    setValidDate(selectedYear, monthIndex, selectedDay);
  };

  const changeDay = (day) => {
    setSelectedDay(day);
    const monthIndex = selectedMonth !== null ? months.indexOf(selectedMonth) : null;
    setValidDate(selectedYear, monthIndex, day);
  };

  const isValidDay = (day) => {
    if (selectedYear === null || selectedMonth === null) {
      return true;
    }

    const selectedDate = new Date(selectedYear, months.indexOf(selectedMonth), day);

    if ((maximumDate !== null && selectedDate > maximumDate) || (minimumDate !== null && selectedDate < minimumDate)) {
      if (day === selectedDay) {
        setSelectedDay(null);
      }
      return false;
    }

    return true;
  };

  const isValidMonth = (month) => {
    if (selectedYear === null) {
      return true;
    }

    const selectedDate = new Date(selectedYear, months.indexOf(month), 1);
    const selectedmonthEndDate = new Date(selectedYear, months.indexOf(month) + 1, 0);

    if ((maximumDate !== null && selectedDate > maximumDate) || (minimumDate !== null && selectedmonthEndDate < minimumDate)) {
      if (month === selectedMonth) {
        setSelectedMonth(null);
      }
      return false;
    }

    return true;
  };

  const isValidYear = (year) => {
    if (minimumDate !== null && year < minimumDate.getFullYear()) {
      return false;
    }

    return true;
  };

  const calculateAge = (birthday) => {
    const todaysDate = new Date();
    const dateOfBirth = convertToDate(birthday);
    const diff = todaysDate.getTime() - dateOfBirth.getTime();
    const numberOfYears = Math.floor(diff / 31556736000);
    const daysDifference = Math.floor((diff % 31556736000) / 86400000);
    const numberOfMonths = Math.floor(daysDifference / 30.4167);

    if (numberOfYears < 1 && numberOfMonths < 1) {
      return `${ daysDifference } day${ daysDifference > 1 ? 's' : '' }`;
    }

    if (numberOfYears < 1) {
      return `${ numberOfMonths } month${ numberOfMonths > 1 ? 's' : '' }`;
    }

    return `${ numberOfYears } year${ numberOfYears > 1 ? 's' : '' }`;
  };

  useEffect(() => {
    const newMaxDate = convertToDate(maxDate);
    setMaximumDate(newMaxDate);
    setCurrentYear(newMaxDate !== null ? newMaxDate.getFullYear() : new Date().getFullYear());
    setCurrentMonth(newMaxDate !== null ? newMaxDate.getMonth() : new Date().getMonth());
  }, [maxDate]);

  useEffect(() => {
    setMinimumDate(convertToDate(minDate));
  }, [minDate]);

  useEffect(() => {
    if (value) {
      setDays(getDaysArray(convertToDate(value).getFullYear(), convertToDate(value).getMonth()));
    }
  }, [value]);

  return (
    <div id={ `dropdown-wrapper-${ id }` } name={ name !== null ? `-dropdown-wrapper-${ name }` : '' } className={ `date-dropdown ${ className }` }>
      {/* add prefix to id and name */}
      <select name="days" id={ `${ id }-day` } className="col-3 col-sm-3 col-md-2" value={ selectedDay === null ? '' : selectedDay } onChange={ e => changeDay(e.target.value) }>
        <option value="">DD</option>
        { days.map((day, index) => isValidDay(day) && <option key={ `item - ${ day }` } value={ day }>
          { day }
        </option>)}
      </select>
      <select name="months" id={ `${ id }-month` } className="col-5 col-md-3 col-sm-3" value={ selectedMonth === null ? '' : selectedMonth } onChange={ e => changeMonth(e.target.value) }>
        <option value="">MM</option>
        { months.map((month, index) => isValidMonth(month) && <option key={ `item - ${ month }` } value={ month }>
          { month }
        </option>)}
      </select>
      <select name="years" id={ `${ id }-year` } className="col-3 col-sm-3 col-md-2" value={ selectedYear === null ? '' : selectedYear } onChange={ e => changeYear(e.target.value) }>
        <option value="">YYYY</option>
        { years.map((yearItem, index) => isValidYear(yearItem) && <option key={ `item - ${ yearItem }` } value={ yearItem }>
          { yearItem }
        </option>) }
      </select>
      { showToolTip && fullDate && <span className="tooltip"><strong>{ tooltipContent === 'date' ? 'Date: ' : 'Age: ' }</strong>{ tooltipContent === 'date' ? fullDate : calculateAge(fullDate) }</span> }
      <input id={ `${ id }-dropdown-value` } name={ `${ name }` } type="hidden" value={ fullDate === null ? '' : fullDate } />
    </div>
  );
};

DateDropdown.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  maxDate: PropTypes.string,
  minDate: PropTypes.string,
  dateFormat: PropTypes.string,
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  showToolTip: PropTypes.bool,
  tooltipContent: PropTypes.string,
  value: PropTypes.string,
};

DateDropdown.defaultProps = {
  name: null,
  maxDate: null,
  minDate: null,
  dateFormat: 'MM-dd-yyyy',
  showToolTip: true,
  className: null,
  tooltipContent: null,
  value: null
};

export default DateDropdown;
